import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BackButton, HomeButton } from '../components/NavButtons';
import {
  CaretDown, MagnifyingGlass, Storefront, CreditCard, ShieldCheck,
  Envelope, Question, User, CalendarBlank, Tag, Star, ChatCircle,
  QrCode, Bell, Megaphone, ArrowRight
} from '@phosphor-icons/react';

const SUPPORT_EMAIL = 'look4myspace@gmail.com';

const FAQ_SECTIONS = [
  {
    id: 'general',
    title: 'General',
    icon: Question,
    color: 'text-blue-500',
    faqs: [
      {
        q: 'What is Look4Space?',
        a: 'Look4Space is a comprehensive space booking platform that connects buyers looking for spaces with sellers who have spaces to offer. We support 20+ categories including parking, coworking, hotels, gyms, event spaces, studios, restaurants, and more.'
      },
      {
        q: 'How do I create an account?',
        a: 'Click "Sign In" on the homepage and choose "Sign In as Buyer" or "Sign In as Host". You can register with your email and password, or sign in with Google for quick access.'
      },
      {
        q: 'Can I be both a buyer and a seller?',
        a: 'Yes! You can switch between Buyer and Seller modes from your Profile page. When you switch, your account is upgraded to support both roles seamlessly.'
      },
      {
        q: 'Is Look4Space available in my country?',
        a: 'Look4Space supports listings worldwide. Sellers can add spaces from any country, and buyers can search and book spaces globally with multi-currency support.'
      },
      {
        q: 'How do I contact support?',
        a: `You can reach us at ${SUPPORT_EMAIL}. We typically respond within 24 hours on business days.`
      },
    ]
  },
  {
    id: 'buyers',
    title: 'For Buyers',
    icon: MagnifyingGlass,
    color: 'text-emerald-500',
    faqs: [
      {
        q: 'How do I search for a space?',
        a: 'Use the search bar on the homepage to search by location, category, or keyword. You can also browse by category using the quick filter pills, or use the advanced search page with filters for price, amenities, and availability.'
      },
      {
        q: 'How do I book a space?',
        a: 'Find a space you like, select your dates and times, choose any add-on services, and click "Book Now". For hourly spaces, you can pick time slots from the visual grid. Some spaces offer instant booking, while others require seller approval.'
      },
      {
        q: 'What are the available time slots?',
        a: 'For hourly-priced spaces, you\'ll see a 24-hour slot grid showing available (green), booked (red), and selected (blue) time slots. Simply click on available slots to select your booking time.'
      },
      {
        q: 'How do I cancel a booking?',
        a: 'Go to "My Bookings" from the bottom navigation or profile. Find your booking in the Upcoming tab, click "Cancel" on the booking card, or open the booking details and click "Cancel Booking". Cancellation policies vary by listing.'
      },
      {
        q: 'Can I share my booking details?',
        a: 'Yes! On the My Bookings page, each booking card has a "Share" button. You can share booking details via your device\'s native share menu or copy them to clipboard.'
      },
      {
        q: 'How does QR check-in work?',
        a: 'Once your booking is confirmed, you\'ll receive a QR code in your booking details. Show this QR code to the seller/host at the venue for quick check-in. You can also use the alphanumeric check-in code.'
      },
      {
        q: 'Can I save spaces to view later?',
        a: 'Yes! Click the heart icon on any listing card to add it to your Saved Spaces (Wishlist). Access your saved spaces from the quick links on the My Bookings page.'
      },
      {
        q: 'How do reviews work?',
        a: 'After completing a booking, you can leave a review with a star rating, tags, and written feedback. Reviews help other buyers make informed decisions and help sellers improve their spaces.'
      },
      {
        q: 'How do I message a seller?',
        a: 'You can message sellers directly through the in-app messaging system. Go to Messages from the bottom navigation to view your conversations.'
      },
    ]
  },
  {
    id: 'sellers',
    title: 'For Sellers / Hosts',
    icon: Storefront,
    color: 'text-orange-500',
    faqs: [
      {
        q: 'How do I list my space?',
        a: 'Switch to Seller Mode from your Profile, then click "Add Listing" on your Seller Dashboard. Select a category, fill in location details, configure space variants with pricing in the spreadsheet-style table, add amenities, images, and publish.'
      },
      {
        q: 'What categories of spaces can I list?',
        a: 'We support 21 categories: Parking, Hotels, Gyms, Yoga/Wellness, Hospital, Home, Coworking, Meeting Rooms, Event Spaces, Studios, Storage, Shops, Malls, Halls, Housing Societies, Religious Places, Salons, Clubs, Restaurants, Sports, and EV Charging.'
      },
      {
        q: 'Can I list spaces in different countries?',
        a: 'Yes! Our global country selection lets you add spaces from any country. Select the country, state, and city when adding your listing location.'
      },
      {
        q: 'How do space configurations (variants) work?',
        a: 'Each category has specific space configurations. For example, Parking has "2-Wheeler", "4-Wheeler", "EV" etc. You enable the ones you offer, set regular and holiday prices, and can add photos/videos per variant.'
      },
      {
        q: 'Can I offer coupons and discounts?',
        a: 'Yes! When creating or editing a listing, use the "Coupons & Discounts" section to create coupon codes with percentage or fixed discounts, minimum order amounts, max discount caps, and validity dates.'
      },
      {
        q: 'How do I promote my listing?',
        a: 'Click the "Promote" button on your Seller Dashboard to access our paid advertising system. Choose from 4 pricing plans (1 Week to 3 Months), create a banner or video ad, select placement, and pay via Stripe checkout. Your ad goes live immediately after payment.'
      },
      {
        q: 'How do I manage bookings?',
        a: 'Your Seller Dashboard shows all incoming bookings. For approval-required listings, you can accept or decline booking requests. Use the QR scanner to check in guests when they arrive.'
      },
      {
        q: 'Can I save my listing as a draft?',
        a: 'Yes! Click "Save Draft" at any point while creating your listing. You can come back later to complete and publish it from your Seller Dashboard.'
      },
      {
        q: 'How do I set different prices for holidays?',
        a: 'In the space configuration table, each variant has both a "Regular Price" and "Holiday Price" column. Set your holiday rates separately to automatically charge more during peak times.'
      },
    ]
  },
  {
    id: 'payments',
    title: 'Payments & Pricing',
    icon: CreditCard,
    color: 'text-purple-500',
    faqs: [
      {
        q: 'What payment methods are accepted?',
        a: 'We accept all major credit cards, debit cards, and digital wallets through our secure Stripe payment processing. Payments are processed in the listing\'s currency.'
      },
      {
        q: 'How does multi-currency support work?',
        a: 'Sellers can set their listing currency (INR, USD, EUR, GBP, AUD, CAD, etc.). Buyers see prices in the listing\'s currency. The platform handles currency display automatically.'
      },
      {
        q: 'When am I charged for a booking?',
        a: 'For instant bookings, payment is collected at the time of booking. For approval-required bookings, payment is collected after the seller approves your request.'
      },
      {
        q: 'What is the cancellation policy?',
        a: 'Cancellation policies vary by listing and can be Flexible, Moderate, or Strict. Check the specific listing\'s cancellation policy before booking. You can cancel eligible bookings from your My Bookings page.'
      },
      {
        q: 'How much does it cost to promote my listing?',
        a: 'Seller ad campaigns start at $9.99 for 1 week, $17.99 for 2 weeks, $29.99 for 1 month, and $74.99 for 3 months. You can choose banner or video ads and select specific placements.'
      },
    ]
  },
  {
    id: 'safety',
    title: 'Safety & Trust',
    icon: ShieldCheck,
    color: 'text-teal-500',
    faqs: [
      {
        q: 'Is my payment information secure?',
        a: 'Yes. All payments are processed through Stripe, a PCI DSS Level 1 certified payment processor. We never store your full card details on our servers.'
      },
      {
        q: 'How are listings verified?',
        a: 'Admins review and can activate/deactivate listings. The platform also features a review and rating system where buyers can share their experiences to help maintain quality standards.'
      },
      {
        q: 'Can I report a problem with a space?',
        a: `If you encounter any issues with a booking or space, contact us at ${SUPPORT_EMAIL} with your booking ID and details. We\'ll investigate and help resolve the issue.`
      },
      {
        q: 'How do push notifications work?',
        a: 'We send browser push notifications for important booking events (confirmations, approvals, cancellations). You can enable/disable notifications from your browser settings.'
      },
    ]
  },
];

const FAQItem = ({ faq, isOpen, onToggle }) => (
  <div className="border-b border-slate-100 last:border-0" data-testid={`faq-item`}>
    <button onClick={onToggle} className="w-full flex items-center justify-between py-4 px-1 text-left group" data-testid="faq-toggle">
      <span className={`text-sm font-medium pr-4 ${isOpen ? 'text-slate-900' : 'text-slate-700'} group-hover:text-blue-600 transition-colors`}>{faq.q}</span>
      <CaretDown size={16} className={`text-slate-400 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180 text-blue-500' : ''}`} />
    </button>
    {isOpen && (
      <div className="pb-4 px-1 text-sm text-slate-600 leading-relaxed animate-in fade-in duration-200" data-testid="faq-answer">
        {faq.a}
      </div>
    )}
  </div>
);

const FAQPage = () => {
  const [openItems, setOpenItems] = useState({});
  const [activeSection, setActiveSection] = useState('general');

  const toggleItem = (sectionId, index) => {
    const key = `${sectionId}-${index}`;
    setOpenItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200/50">
        <div className="max-w-4xl mx-auto px-4 h-14 flex items-center gap-2">
          <BackButton />
          <HomeButton />
          <h1 className="text-lg font-bold text-slate-900">Help & FAQ</h1>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-gradient-to-b from-blue-50 to-slate-50 py-10 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2" data-testid="faq-title">Frequently Asked Questions</h2>
          <p className="text-slate-500 text-sm max-w-lg mx-auto">Everything you need to know about booking and listing spaces on Look4Space</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Section Tabs */}
        <div className="flex gap-1.5 overflow-x-auto pb-4 mb-2 scrollbar-hide" data-testid="faq-tabs">
          {FAQ_SECTIONS.map(section => {
            const Icon = section.icon;
            const isActive = activeSection === section.id;
            return (
              <button key={section.id} onClick={() => setActiveSection(section.id)}
                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-all ${
                  isActive
                    ? 'bg-blue-500 text-white shadow-sm shadow-blue-200'
                    : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-300'
                }`}
                data-testid={`faq-tab-${section.id}`}>
                <Icon size={14} weight={isActive ? 'fill' : 'regular'} />
                {section.title}
              </button>
            );
          })}
        </div>

        {/* FAQ Content */}
        {FAQ_SECTIONS.map(section => {
          if (section.id !== activeSection) return null;
          const Icon = section.icon;
          return (
            <div key={section.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`faq-section-${section.id}`}>
              <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
                <Icon size={18} className={section.color} weight="fill" />
                <h3 className="text-sm font-bold text-slate-800">{section.title}</h3>
                <span className="text-[10px] text-slate-400 ml-1">{section.faqs.length} questions</span>
              </div>
              <div className="px-4">
                {section.faqs.map((faq, i) => {
                  const faqKey = `${section.id}-${faq.q ? faq.q.slice(0, 40).toLowerCase().replace(/\W+/g, '-') : i}`;
                  return (
                    <FAQItem key={faqKey} faq={faq} isOpen={!!openItems[`${section.id}-${i}`]}
                      onToggle={() => toggleItem(section.id, i)} />
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Contact Section */}
        <div className="mt-8 bg-white rounded-xl border border-slate-200 p-6 text-center" data-testid="faq-contact">
          <Envelope size={32} className="text-blue-400 mx-auto mb-2" />
          <h3 className="text-base font-bold text-slate-900 mb-1">Still have questions?</h3>
          <p className="text-sm text-slate-500 mb-3">We're here to help. Reach out to our support team.</p>
          <a href={`mailto:${SUPPORT_EMAIL}`}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-xl text-sm font-semibold transition-colors"
            data-testid="contact-email-btn">
            <Envelope size={16} /> {SUPPORT_EMAIL}
          </a>
        </div>

        {/* Quick Links */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
          <Link to="/buyer" className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200 hover:border-blue-300 transition-colors group" data-testid="link-explore-spaces">
            <MagnifyingGlass size={20} className="text-blue-400" />
            <div>
              <p className="text-sm font-semibold text-slate-800 group-hover:text-blue-600">Explore Spaces</p>
              <p className="text-[11px] text-slate-400">Browse all categories</p>
            </div>
            <ArrowRight size={14} className="text-slate-300 ml-auto" />
          </Link>
          <Link to="/seller/login" className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200 hover:border-emerald-300 transition-colors group" data-testid="link-list-space">
            <Storefront size={20} className="text-emerald-400" />
            <div>
              <p className="text-sm font-semibold text-slate-800 group-hover:text-emerald-600">List Your Space</p>
              <p className="text-[11px] text-slate-400">Start earning today</p>
            </div>
            <ArrowRight size={14} className="text-slate-300 ml-auto" />
          </Link>
          <a href={`mailto:${SUPPORT_EMAIL}`} className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200 hover:border-purple-300 transition-colors group" data-testid="link-contact">
            <Envelope size={20} className="text-purple-400" />
            <div>
              <p className="text-sm font-semibold text-slate-800 group-hover:text-purple-600">Contact Us</p>
              <p className="text-[11px] text-slate-400">{SUPPORT_EMAIL}</p>
            </div>
            <ArrowRight size={14} className="text-slate-300 ml-auto" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;
